import { Check, ChevronsUpDown, Info, Loader2, Search } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from './ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command'
import { Label } from './ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

interface MySearchSelectOptions {
  id?: string | number | undefined
  value?: string | number | undefined
  name: string
}

type SearchSelectProps = {
  label?: string;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  tooltipContent?: string;
  value?: string | undefined;
  options: MySearchSelectOptions[];
  onValueChange: (value: string) => void;
  id?: string;
} & React.HTMLAttributes<HTMLDivElement>

function SearchSelect(props: SearchSelectProps) {
  const { id, label, value, options, onValueChange, placeholder = 'Select option...', tooltipContent, isLoading, disabled, className, ...rest } = props
  const [open, setOpen] = React.useState(false)

  const selectedOption = options.find(
    option => (option.value?.toString() || option.id?.toString()) === value,
  )

  return (
    <div className={cn('space-y-2', className)} {...rest}>
      {label && (
        <div className="flex items-center gap-2">
          <Label htmlFor={id}>{label}</Label>
          {tooltipContent && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  aria-label="Information"
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {tooltipContent}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isLoading}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">
              {selectedOption?.name || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
          <Command className="rounded-lg border-0">
            <div className="relative border-b mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/60" />
              <CommandInput className="pl-2 border-2 mb-4 focus:ring-0 " placeholder={`Search ${label?.toLowerCase() || 'options'}...`} />
              {isLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <CommandList className="max-h-[280px]">
              <CommandEmpty>
                {isLoading ? 'Loading...' : 'No option found.'}
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const optionValue = option.value?.toString() || option.id?.toString() || ''
                  return (
                    <CommandItem
                      // className="flex items-center gap-3 py-3"
                      key={option.id ?? Math.random().toString(16).slice(2)}
                      value={option.name}
                      onSelect={() => {
                        onValueChange(optionValue)
                        setOpen(false)
                      }}
                    >
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0',
                          optionValue === value
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      {option.name}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default SearchSelect
